import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Row, Col, Card, Table, InputRef, Input, Button, Tag, AutoComplete } from 'antd'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import { SearchOutlined, CloseOutlined, DeleteFilled, SaveFilled } from '@ant-design/icons'
import TableModIcon from 'Components/Svg/TableMod'
import {
  getallTags,
  postdeleteTagCode,
  postdeleteTagValue,
  postupdateTagCode,
  postupdateTagValue,
} from 'Services/Utility/UtilityService'
import { useAppDispatch } from 'hooks'

function Utility() {
  const dispatch = useAppDispatch()
  type DataItem = { key: string; nome: string }
  const [columnsTable, setColumnsTable] = useState<ColumnsType<any>>([])
  const [searchData, setSearchData] = useState<DataItem[]>([])
  const [searchedColumns, setSearchedColumns] = useState<any[]>([])
  const [cTagData, setCTagData] = useState<DataItem[]>([])
  const [tagfull, setTagFull] = useState<any[]>([])
  const [searchInputVisible, setSearchInputVisible] = useState<string | null>(null)
  const searchInput = useRef<InputRef>(null)
  const [modifiedCtag, setModifiedCtag] = useState<{ row: string; col: string; value: any }[]>([])
  const [focusedRow, setFocusedRow] = useState<string | null>(null)
  const [editSingleRow, setEditSingleRow] = useState(false)
  const [columnsData, setColumnsData] = useState<any[]>([])
  const buttonRef = useRef<HTMLDivElement>(null)
  const tableRowRef = useRef<HTMLDivElement>(null)
  const [modTags, setModTags] = useState<{ key: string; value: string }[]>([])
  const [editIndexTAG, setEditIndexTAG] = useState<number | null>(null)
  const [editValueTAG, setEditValueTAG] = useState('')

  const loadData = useCallback(() => {
    dispatch(getallTags()).then(data => {
      const tempTagFull = []
      const tempCTagData = []

      data.forEach(el => {
        if (el.value !== '') {
          const existingIndex = tempCTagData.findIndex(item => item.nome === el.code)
          if (existingIndex !== -1) {
            tempCTagData.splice(existingIndex, 1, { key: el.code, nome: el.code })
          } else {
            tempCTagData.push({ key: el.code, nome: el.code })
          }
        }
        tempTagFull.push({ id: el.slug, value: el.slug })
      })

      setTagFull(tempTagFull)
      setCTagData(tempCTagData)
    })
  }, [dispatch])

  useEffect(() => {
    loadData()
  }, [dispatch])

  const resetOutSide = useCallback(() => {
    setEditSingleRow(false)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (
        tableRowRef.current &&
        !tableRowRef.current.contains(target) &&
        !target.classList?.contains('ant-table-cell')
      ) {
        resetOutSide()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        resetOutSide()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [tableRowRef, buttonRef, resetOutSide])

  useEffect(() => {
    let filteredData = cTagData
    if (searchedColumns.length > 0) {
      filteredData = cTagData.filter(item =>
        searchedColumns.every(col => {
          const itemValue = item[col.dataIndex]
          if (!itemValue && col.value === '') return true
          else return itemValue.toString().toLowerCase().includes(col.value.toLowerCase())
        }),
      )
    }
    setSearchData(filteredData)
    setColumnsData([
      { key: 'ctag', nome: '', dataIndex: 'nome', title: 'cTag' },
      {
        key: 'delete',
        title: '',
        dataIndex: 'delete',
      },
    ])
  }, [cTagData, searchedColumns])

  const handleRowClick = useCallback((e: React.MouseEvent, row: string, col: string) => {
    resetOutSide()
    setFocusedRow(row)
  }, [])

  const handleSingleModClick = useCallback(() => {
    setEditSingleRow(true)
  }, [])

  const handleInputChange = useCallback((oldCode: string, newCode: string, deleteCod: Boolean) => {
    if (deleteCod) setCTagData(prev => prev.filter(item => item.key !== oldCode))
    setModifiedCtag(prev => {
      const existing = prev.find(item => item.row === oldCode && item.col === 'cTag')
      if (existing) {
        return prev.map(item => (item.row === oldCode && item.col === 'cTag' ? { ...item, value: newCode } : item))
      }
      return [...prev, { row: oldCode, col: 'cTag', value: newCode }]
    })
  }, [])

  const resetSearch = useCallback((dataIndex: string) => {
    setSearchedColumns(prev => prev.filter(col => col.dataIndex !== dataIndex))
  }, [])

  const isEditing = useMemo(() => {
    return (recordKey: string, colKey: string) => recordKey === focusedRow && editSingleRow
  }, [focusedRow, editSingleRow])

  const handleSearch = useCallback((value: string, dataIndex: string) => {
    setSearchedColumns(pre => {
      const existingIndex = pre.findIndex(item => item.dataIndex === dataIndex)
      return existingIndex !== -1
        ? [...pre.slice(0, existingIndex), { dataIndex: dataIndex, value: value }, ...pre.slice(existingIndex + 1)]
        : [...pre, { dataIndex: dataIndex, value: value }]
    })
  }, [])

  const getColumnSearchProps = useCallback(
    (dataIndex: string, title: string, width: number): ColumnType<any> => {
      const columnSearch = searchedColumns.find(e => e.dataIndex === dataIndex)
      const searchText = columnSearch ? columnSearch.value : ''

      return {
        filterDropdown: () =>
          searchInputVisible ? (
            <div style={{ padding: 6 }}>
              <Input
                ref={searchInput}
                onChange={e => handleSearch(e.target.value, dataIndex)}
                style={{ marginBottom: 3, display: 'block', width: width }}
                value={searchText}
              />
            </div>
          ) : null,
        filterIcon: searchText ? (
          <CloseOutlined onClick={() => resetSearch(dataIndex)} style={{ marginRight: 10, color: 'red' }} />
        ) : (
          <SearchOutlined
            style={{ marginRight: 10, fontSize: '14px', color: 'white' }}
            onClick={() => setSearchInputVisible(dataIndex)}
          />
        ),
        onFilterDropdownOpenChange: visible => {
          if (visible) {
            setTimeout(() => searchInput.current?.select(), 100)
          }
        },
        title: searchText ? <span style={{ color: 'red' }}>({searchText})</span> : title,
      }
    },
    [handleSearch, resetSearch, searchedColumns, searchInputVisible],
  )

  useEffect(() => {
    setColumnsTable(
      columnsData.map((col, index, arr) => {
        const width = index === 0 ? 300 : 40
        return {
          ...col,
          ...(index === 0 ? getColumnSearchProps(col.dataIndex, col.title, width) : null),
          width: width,
          onHeaderCell: () => ({
            style: {
              whiteSpace: 'normal',
              wordWrap: 'normal',
              height: '25px',
            },
          }),
          onCell: () => ({
            style: {
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            },
          }),
          fixed: index === 0 ? 'left' : index === arr.length - 1 ? 'center' : undefined,
          render: (text: string, record: any) => {
            const modifiedValue = modifiedCtag.find(item => item.row === text)
            const displayText = modifiedValue ? modifiedValue.value : text
            const isModified = !!modifiedValue

            if (isEditing(record.key, col.dataIndex)) {
              return index === 0 ? (
                <Input defaultValue={displayText} onChange={e => handleInputChange(text, e.target.value, false)} />
              ) : null
            } else {
              return (
                <div
                  style={
                    isModified
                      ? {
                          fontWeight: 'bold',
                          boxShadow: '0px 0px 1px 1px rgba(255, 0, 0, 0.7)',
                          display: 'flex',
                          alignItems: 'center',
                        }
                      : {
                          display: 'flex',
                          alignItems: 'center',
                        }
                  }
                >
                  {index === 0 ? (
                    <>
                      <div style={{ paddingRight: '8px' }}>
                        <Button
                          onClick={e => (handleSingleModClick(), setFocusedRow(record.key))}
                          style={{ width: '20px', paddingLeft: '2px' }}
                        >
                          <TableModIcon height='14px' />
                        </Button>
                      </div>
                      <div onClick={e => handleRowClick(e, record.key, col.dataIndex)}>{displayText}</div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        icon={<DeleteFilled />}
                        onClick={e => handleInputChange(record.nome, 'delete', true)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      />
                    </div>
                  )}
                </div>
              )
            }
          },
        }
      }),
    )
  }, [handleRowClick, isEditing, modifiedCtag, getColumnSearchProps, handleInputChange, columnsData])

  const saveChanges = async () => {
    const promises = modifiedCtag.map(el => {
      if (el.col === 'cTag' || (el.col === 'fullTag' && !el.row.includes('_'))) {
        if (el.value === 'delete') {
          return dispatch(postdeleteTagCode(el.row))
        } else {
          return dispatch(postupdateTagCode(el.row, el.value))
        }
      }

      if (el.col === 'fullTag' && el.row.includes('_')) {
        if (el.value === 'delete') {
          return dispatch(postdeleteTagValue(el.row.split('_')[0], el.row.split('_')[1]))
        } else {
          return dispatch(postupdateTagValue(el.row.split('_')[0], el.row.split('_')[1], el.value.split('_')[1]))
        }
      }
    })

    await Promise.all(promises)
    setModifiedCtag([])
    loadData()
  }

  const handleSearchTAG = (value: string) => {
    if (value && !modTags.some(tag => tag.value === value)) {
      setModTags([...modTags, { key: Date.now().toString(), value }])
    }
  }

  const handleEditTAG = (index: number) => {
    setEditIndexTAG(index)
    setEditValueTAG(modTags[index].value)
  }

  const handlechangeTAG = useCallback(
    (index: number, delCod: Boolean) => {
      index = delCod ? index : editIndexTAG
      if (index !== null) {
        const newTags = [...modTags]
        const oldvalue = modTags[index].value
        setEditIndexTAG(null)
        setEditValueTAG('')
        setModifiedCtag(prev => {
          const existing = prev.find(item => item.value === oldvalue && item.col === 'fullTag')
          if (existing) {
            return prev.map(item =>
              item.value === oldvalue && item.col === 'fullTag'
                ? { ...item, value: delCod ? 'delete' : editValueTAG }
                : item,
            )
          }
          return [...prev, { row: oldvalue, col: 'fullTag', value: delCod ? 'delete' : editValueTAG }]
        })
        newTags[index].value = delCod ? oldvalue + '-delete' : editValueTAG
        setModTags(newTags)
        setTagFull(prev => {
          const existingIndex = prev.findIndex(item => item.value === oldvalue)
          return existingIndex !== -1
            ? [
                ...prev.slice(0, existingIndex),
                { id: oldvalue, value: delCod ? 'delete' : editValueTAG },
                ...prev.slice(existingIndex + 1),
              ]
            : prev
        })
      }
    },
    [editIndexTAG, editValueTAG, modTags],
  )

  const handlehideTAG = (key: string) => {
    setModTags(modTags.filter(tag => tag.key !== key))
  }

  const handleRollBackTAG = useCallback(
    (tagvalue: string) => {
      const modItem = modifiedCtag.find(
        item =>
          (item.value === tagvalue && item.col === 'fullTag') ||
          (item.row === tagvalue.replace('-delete', '') && item.col === 'fullTag' && item.value === 'delete'),
      )
      setTagFull(prev => {
        const existingIndex = prev.findIndex(
          item =>
            (item.value === tagvalue && item.col === 'fullTag') ||
            (item.row === tagvalue.replace('-delete', '') && item.col === 'fullTag' && item.value === 'delete'),
        )
        return existingIndex !== -1
          ? [...prev.slice(0, existingIndex), { id: modItem.row, value: modItem.row }, ...prev.slice(existingIndex + 1)]
          : prev
      })
      setModifiedCtag(
        modifiedCtag.filter(
          item =>
            item.value !== tagvalue &&
            item.col === 'fullTag' &&
            item.row !== tagvalue.replace('-delete', '') &&
            item.col === 'fullTag' &&
            item.value === 'delete',
        ),
      )

      const index = modTags.findIndex(tag => tag.value === tagvalue)

      if (index !== -1) {
        modTags[index].value = modItem.row
        setModTags(modTags)
      }
    },
    [modTags, modifiedCtag],
  )

  const Tags = useMemo(() => {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {modTags.map((tag, index) => {
          const isModified = modifiedCtag.some(item => item.value === tag.value && item.col === 'fullTag')
          const isDeleted = modifiedCtag.some(
            item => item.row === tag.value.replace('-delete', '') && item.col === 'fullTag' && item.value === 'delete',
          )
          return (
            <Tag key={tag.key} style={{ margin: 4 }}>
              <div style={{ alignItems: 'center', display: 'flex', padding: 4 }}>
                {editIndexTAG === index ? (
                  editValueTAG.includes('_') ? (
                    <div style={{ alignItems: 'center', display: 'flex' }}>
                      {editValueTAG.split('_')[0] + '_'}
                      <Input
                        value={editValueTAG.split('_')[1]}
                        onChange={e => setEditValueTAG(editValueTAG.split('_')[0] + '_' + e.target.value)}
                        onPressEnter={e => handlechangeTAG(index, false)}
                        onBlur={e => handlechangeTAG(index, false)}
                        style={{ fontSize: 14, padding: 0, paddingLeft: 2 }}
                      />
                    </div>
                  ) : (
                    <Input
                      value={editValueTAG}
                      onChange={e => setEditValueTAG(e.target.value)}
                      onPressEnter={e => handlechangeTAG(index, false)}
                      onBlur={e => handlechangeTAG(index, false)}
                      width='auto'
                      style={{ margin: 4 }}
                    />
                  )
                ) : (
                  <span style={{ fontSize: 14, marginRight: 15 }}>{tag.value}</span>
                )}
                {editIndexTAG !== index && !isDeleted && (
                  <>
                    <TableModIcon
                      onClick={() => handleEditTAG(index)}
                      style={{
                        cursor: 'pointer',
                        width: '14px',
                        height: '14px',
                        marginRight: 15,
                      }}
                    />
                    <DeleteFilled
                      onClick={e => handlechangeTAG(index, true)}
                      style={{ cursor: 'pointer', marginRight: 13 }}
                    />
                  </>
                )}
                {isModified || isDeleted ? (
                  <div
                    onClick={() => handleRollBackTAG(tag.value)}
                    style={{ cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}
                  >
                    R
                  </div>
                ) : (
                  <CloseOutlined onClick={() => handlehideTAG(tag.key)} style={{ cursor: 'pointer', fontSize: 13 }} />
                )}
              </div>
            </Tag>
          )
        })}
      </div>
    )
  }, [
    editIndexTAG,
    editValueTAG,
    handleEditTAG,
    handlechangeTAG,
    handlehideTAG,
    modTags,
    modifiedCtag,
    handleRollBackTAG,
  ])

  return (
    <Row gutter={16}>
      <Col style={{ margin: '20px' }}>
        <Card
          title={
            <div className='card-header'>
              <span className='card-title'>Utility Tag</span>
              <SaveFilled
                className={'generic-icon save-icon channel-save-icon' + (modifiedCtag.length > 0 ? ' active' : '')}
                onClick={modifiedCtag.length > 0 ? saveChanges : null}
              />
            </div>
          }
          bordered={false}
          style={{ width: 400 }}
        >
          <Row>
            <div className='table-wrapper' ref={tableRowRef}>
              <Table
                className='table-settings'
                pagination={false}
                tableLayout='fixed'
                columns={columnsTable || []}
                dataSource={searchData}
                rowKey='key'
              />
            </div>
          </Row>
          <Row>
            <div style={{ width: '100%', marginTop: '30px' }}>
              <div className='labeltag'>full Tag</div>
              <div>
                <AutoComplete
                  onSelect={e => handleSearchTAG(e)}
                  options={tagfull.filter(item => item.value !== 'delete')}
                  filterOption={(inputValue, option) =>
                    option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                  style={{ width: '100%', marginBottom: 6 }}
                  placeholder={<SearchOutlined />}
                />

                {Tags}
              </div>
            </div>
          </Row>
        </Card>
      </Col>
    </Row>
  )
}

export default Utility
